import React, { useState, useEffect } from "react";
import { db } from "../firebase.config";
import { Box, useColorModeValue, Heading, Text, Badge, Flex, Image, Stack, Skeleton, SkeletonText } from "@chakra-ui/react";
import { getDoc, doc, getDocs, collection } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { FadeLoader } from "react-spinners";
import { Helmet } from "react-helmet";
import { ImLocation, ImPhone } from "react-icons/im";
import { FaWhatsapp, FaStore, FaCar, FaCalendarAlt, FaTachometerAlt, FaTag } from "react-icons/fa";
import { BsStarFill, BsStarHalf, BsStar } from "react-icons/bs";
import { Link } from "react-router-dom";

const SellerProfile = () => {
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { sellerId } = useParams();
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchSellerProfileAndListings = async () => {
      setLoading(true);
      try {
        const sellerDocRef = doc(db, "Feature-Seller", sellerId);
        const sellerDocSnap = await getDoc(sellerDocRef);

        if (sellerDocSnap.exists()) {
          setSeller(sellerDocSnap.data());
        } else {
          setError("No such seller found");
        }

        const listingsRef = collection(db, "Feature-Seller", sellerId, "listings");
        const listingsSnapshot = await getDocs(listingsRef);
        const listingsList = listingsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        listingsList.sort((a, b) => a.ranking - b.ranking);
        setListings(listingsList);
      } catch (error) {
        setError("Error fetching seller profile: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerProfileAndListings();
  }, [sellerId]);

  // Format price with commas
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Render star rating
  const renderStars = (ranking) => {
    const stars = [];
    const fullStars = Math.floor(ranking);
    const hasHalfStar = ranking % 1 >= 0.5;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<BsStarFill key={i} className="text-yellow-400" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<BsStarHalf key={i} className="text-yellow-400" />);
      } else {
        stars.push(<BsStar key={i} className="text-gray-300" />);
      }
    }
    return stars;
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex justify-center items-center">
      <FadeLoader color="#3182ce" />
    </div>
  );

  if (error) return (
    <Box className="text-center p-8 min-h-screen bg-gray-50">
      <Heading size="lg" className="text-red-500 mb-4">{error}</Heading>
      <Link to="/" className="text-blue-500 hover:underline">Return to homepage</Link>
    </Box>
  );

  if (!seller) return (
    <Box className="text-center p-8 min-h-screen bg-gray-50">
      <Heading size="lg" className="text-gray-600 mb-4">Showroom not found</Heading>
      <Link to="/" className="text-blue-500 hover:underline">Browse other sellers</Link>
    </Box>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <Helmet>
        <title>{`${seller.sellerName} | NCPWheels`}</title>
        <meta
          name="description"
          content={`Explore premium vehicles from ${seller.sellerName}, a trusted auto dealer on NCPWheels.`}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>

        {/* Seller Profile Header */}
        <Box className={`bg-white rounded-2xl shadow-lg overflow-hidden mb-8`}>
          <div className="relative">
            {/* Banner Image */}
            <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600"></div>
            
            {/* Profile Section */}
            <div className="px-6 pb-6 relative">
              <div className="flex flex-col md:flex-row items-center md:items-end -mt-12">
                {/* Logo/Thumbnail */}
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-xl overflow-hidden bg-white">
                  {seller.thumbnail ? (
                    <img
                      src={seller.thumbnail}
                      alt={seller.sellerName || seller.businessName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                      <FaStore className="text-3xl text-blue-500" />
                    </div>
                  )}
                </div>
                
                <div className="mt-4 md:mt-0 md:ml-6 text-center md:text-left flex-1">
                  <Heading as="h1" size="xl" className="text-gray-900">
                    {seller.sellerName}
                  </Heading>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <Text className="text-gray-600">
                      {seller.businessName ? seller.businessName : "Individual Seller"}
                    </Text>
                    {seller.verified && (
                      <Badge colorScheme="green" className="ml-2">
                        ✓ Verified
                      </Badge>
                    )}
                    {seller.specialization && (
                      <Badge colorScheme="blue" className="ml-2">
                        {seller.specialization}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <Text className="text-gray-600 flex items-center">
                      <ImLocation className="mr-1 text-blue-500" />
                      {seller.address || seller.city}
                    </Text>
                    {seller.ranking && (
                      <div className="flex items-center gap-1">
                        {renderStars(seller.ranking)}
                        <span className="text-sm text-gray-600 ml-1">
                          ({seller.ranking})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Info */}

              {/* Description */}
              {seller.description && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <Text className="text-gray-700">{seller.description}</Text>
                </div>
              )}
            </div>
          </div>
        </Box>

        {/* Listings Section */}
        <div className="mb-6">
          <Heading as="h2" size="lg" className="text-gray-800 flex items-center gap-2">
            <FaCar className="text-blue-500" />
            Available Vehicles
            <Badge colorScheme="blue" className="ml-2">
              {listings.length}
            </Badge>
          </Heading>
        </div>

        {listings.length === 0 ? (
          <Box className="text-center py-16 px-4 bg-white rounded-2xl shadow-md">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCar className="text-4xl text-gray-400" />
            </div>
            <Heading as="h3" size="md" className="text-gray-700 mb-2">
              No Vehicles Available
            </Heading>
            <Text className="text-gray-500 max-w-md mx-auto">
              This showroom currently has no vehicles listed. Please check back later or explore other showrooms.
            </Text>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition duration-200"
            >
              Browse Other Vehicles
            </Link>
          </Box>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((car) => (
              <Box
                key={car.id}
                className={`bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer`}
                onClick={() => navigate(`/category/sale/${car.id}?sellerId=${sellerId}&type=featured-seller`)}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-gray-200">
                  <img
                    src={car.carImages?.[0] || "/placeholder-car.jpg"}
                    alt={`${car.manufacturer} ${car.modelName}`}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  {car.offer && (
                    <Badge 
                      colorScheme="red" 
                      className="absolute top-3 right-3 text-xs px-2 py-1"
                    >
                      🔥 Offer
                    </Badge>
                  )}
                  {car.ranking && (
                    <Badge 
                      colorScheme="yellow" 
                      className="absolute top-3 left-3 text-xs px-2 py-1"
                    >
                      ⭐ {car.ranking}
                    </Badge>
                  )}
                </div>

                {/* Details */}
                <div className="p-4">
                  <Heading as="h3" size="sm" className="text-gray-900 mb-1 truncate">
                    {car.manufacturer} {car.modelName}
                  </Heading>
                  <Text className="text-gray-600 text-sm mb-2 truncate">
                    {car.modelYear} • {car.transmission}
                  </Text>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Price</span>
                      <span className="font-semibold text-blue-600">
                        {formatPrice(car.price)}
                      </span>
                    </div>
                    
                    {car.discountedPrice && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Discounted</span>
                        <span className="font-semibold text-green-600">
                          {formatPrice(car.discountedPrice)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Mileage</span>
                      <span className="text-gray-700">{car.mileage || 'N/A'} km</span>
                    </div>

                    {car.city && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Location</span>
                        <span className="text-gray-700 truncate">{car.city}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/category/sale/${car.id}?sellerId=${sellerId}&type=featured-seller`);
                      }}
                      className="flex-1 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition duration-200"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </Box>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerProfile;